import { createContext, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import i18n, { applyLanguageDir } from "@/lib/i18n";

const ProContext = createContext(null);

export const ProProvider = ({ children }) => {
  const { user, updateUser, refresh } = useAuth();
  const { i18n: i18nInstance } = useTranslation();

  // Sync language with user profile
  useEffect(() => {
    const preferred = user?.language || localStorage.getItem("stb_language") || "en";
    if (preferred !== i18nInstance.language) {
      i18nInstance.changeLanguage(preferred);
    }
    applyLanguageDir(preferred);
  }, [user?.language, i18nInstance]);

  // Sync privacy mode
  useEffect(() => {
    const on = !!user?.privacy_mode;
    document.documentElement.setAttribute("data-privacy", on ? "true" : "false");
  }, [user?.privacy_mode]);

  const activatePro = async () => {
    const token = localStorage.getItem("stb_token");
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pro/activate`, {
      method: "POST", headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error("Failed");
    await refresh();
  };

  const dismissBanner = async () => {
    const token = localStorage.getItem("stb_token");
    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pro/dismiss-banner`, {
      method: "POST", headers: { Authorization: `Bearer ${token}` },
    });
    await refresh();
  };

  const setLanguage = async (code) => {
    localStorage.setItem("stb_language", code);
    i18n.changeLanguage(code);
    if (user) await updateUser({ language: code });
  };

  const setPrivacyMode = async (on) => {
    document.documentElement.setAttribute("data-privacy", on ? "true" : "false");
    if (user) await updateUser({ privacy_mode: on });
  };

  return (
    <ProContext.Provider value={{
      isPro: !!user?.is_pro,
      bannerDismissed: !!user?.banner_dismissed,
      privacyMode: !!user?.privacy_mode,
      language: user?.language || i18nInstance.language || "en",
      activatePro,
      dismissBanner,
      setLanguage,
      setPrivacyMode,
    }}>
      {children}
    </ProContext.Provider>
  );
};

export const usePro = () => {
  const ctx = useContext(ProContext);
  if (!ctx) throw new Error("usePro must be used within ProProvider");
  return ctx;
};
